"use client"
import dynamic from "next/dynamic"
import { memo, Suspense, useMemo } from "react"
// Lazy: Globe2 pulls in the whole three.js stack (~270KB gz — three,
// @react-three/fiber, react-reconciler, every globe layer), by far the
// page's largest bundle block. It's a WebGL canvas with no SSR output, so
// load it after hydration instead of in First Load JS; the sphere pops in
// once the chunk arrives.
const Globe2 = dynamic(() => import("@/app/playbook/globe/Globe2WithTimeline"), { ssr: false })
import type { PlanId } from "@/lib/branchTypes"
import { cameraKeyframesData } from "@/app/playbook/globe/cameraSettings"
import {
  type CountryWakeup,
  type DateMarkerPosition,
  type Globe2Settings,
  type StateWakeup,
} from "@/app/playbook/globe/globe2Types"
import { type LegendMarkerPosition } from "@/app/playbook/globe/LegendText"

// Camera picks the "active" keyframe by exact-matching one marker
// date against cameraKeyframesData's keys. The datacenter timeline's
// denser dates mostly miss the sparse keyframe keys, so snapping the
// marker to a timeline date hits a non-existent keyframe and collapses
// to the first (2026 / -15°). So we snap to the latest keyframe key
// on or before the current date instead.
const SORTED_CAMERA_KEYFRAME_DATES: readonly string[] =
  Object.keys(cameraKeyframesData).sort()

type Props = {
  currentDate: Date
  // Branch plans (B/C/D/S) swap in their generated datacenter timelines.
  activePlan?: PlanId | null
  globeSettings?: Globe2Settings
  countryWakeup?: CountryWakeup[]
  stateWakeup?: StateWakeup[]
  legendMarkerPositions?: LegendMarkerPosition[]
  size?: number
  lockSizeToInitialHeight?: boolean
  minDevicePixelRatio?: number
  paused?: boolean
  // True while the branch-hover autoplay is driving the date (the "Choose a
  // Path" teaser). The teaser stops at the forecast proper: the 2041+
  // epilogue zoom-away keyframes are skipped, holding the last visible
  // framing instead of receding into the epilogue.
  isHoverPreviewActive?: boolean
}

export const PlaybookGlobe = memo(({
  currentDate,
  activePlan = null,
  globeSettings,
  countryWakeup,
  stateWakeup,
  legendMarkerPositions,
  size = 400,
  lockSizeToInitialHeight = true,
  minDevicePixelRatio = 1,
  paused = false,
  isHoverPreviewActive = false,
}: Props) => {
  // Snap the camera marker to the latest keyframe key on or before
  // the current date (NOT the latest datacenter date — see
  // SORTED_CAMERA_KEYFRAME_DATES). The active keyframe is the one most
  // recently passed, so dates between keyframes (e.g. 2027-08-31)
  // hold the 2027 keyframe instead of collapsing to the first (2026 /
  // -15°). The 2041+ zoom-away keys (Earth recedes at 2041-01-01 and
  // fades to opacity 0, receding further at 2041-06-01) are Plan A's
  // epilogue hand-off to the galaxy — branch plans (S runs to 2045+
  // with a frozen world) and the branch-hover autoplay teaser skip
  // them and hold the last visible framing.
  const isBranchPlan = activePlan !== null && activePlan !== "A"
  const skipEpilogueKeyframes = isBranchPlan || isHoverPreviewActive
  const matchedCameraDate = useMemo(() => {
    const year = currentDate.getUTCFullYear()
    const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0")
    const day = String(currentDate.getUTCDate()).padStart(2, "0")
    const dateStr = `${year}-${month}-${day}`
    let matched = SORTED_CAMERA_KEYFRAME_DATES[0]
    for (const kfDate of SORTED_CAMERA_KEYFRAME_DATES) {
      if (skipEpilogueKeyframes && kfDate >= "2041-01-01") break
      if (kfDate <= dateStr) matched = kfDate
    }
    return matched
  }, [currentDate, skipEpilogueKeyframes])
  const cameraDateMarkerPositions: DateMarkerPosition[] = useMemo(
    () => [{ dateString: matchedCameraDate, offset: 0, scrollTopPx: 0 }],
    [matchedCameraDate],
  )

  return (
    <div
      style={{
        width: size,
        height: size,
        clipPath: "circle(50% at 50% 50%)",
        isolation: "isolate",
        overflow: "hidden",
        pointerEvents: "none",
        position: "relative",
      }}
    >
      <Suspense fallback={null}>
        <Globe2
          activePlan={activePlan}
          settings={globeSettings}
          scrollPosition={0}
          currentDate={currentDate}
          cameraDateMarkerPositions={cameraDateMarkerPositions}
          legendMarkerPositions={legendMarkerPositions}
          countryWakeup={countryWakeup}
          stateWakeup={stateWakeup}
          lockSizeToInitialHeight={lockSizeToInitialHeight}
          minDevicePixelRatio={minDevicePixelRatio}
          paused={paused}
        />
      </Suspense>
    </div>
  )
})
PlaybookGlobe.displayName = "PlaybookGlobe"
